/**
 * components/EditLogTimeline.js
 * Shared timeline content for a transaction's audit trail: the creation event first
 * ("PERTAMA" tag), then every editLog entry as a "Detail Perubahan" node with its
 * field-by-field old → new diff. Used inside TransactionDetailModal's "Riwayat Perubahan"
 * card (the inline history-clock row expand on Penjualan/Pembelian that used to be this
 * component's other call site was removed in Phase T2b-fix — the detail modal already shows
 * the identical timeline, and a row click already opens it).
 *
 * Corrective pass, Piece 3: PERTAMA is now a REAL, fully-itemized editLog[0] entry for any
 * transaction created after this shipped (App.js's createTransaction, marked `isCreation:
 * true`) — it renders via the exact same `EditLogEntryNode` every DETAIL PERUBAHAN entry uses
 * (same MoneyBlock/ItemChangeBlock/PaymentChangeBlock/CostChangeBlock expanded detail), tagged
 * "PERTAMA" instead of "DETAIL PERUBAHAN" — the tag string plus the collapsed-summary wording
 * are the only differences between the two. The collapsed line originally reused `describeEntry`'s
 * fully generic wording verbatim ("N barang diubah · ... · Total Rp 0 → Rp X", no "Transaksi
 * dibuat" prefix) — that was superseded by the PERTAMA-narrative-sentence follow-up task, once
 * Chalton found the generic "diubah" ("changed") wording nonsensical for a creation entry
 * (nothing was changed, this is simply how the transaction started). The collapsed `<span>` now
 * reads `entry.isCreation ? describeCreation(entry) : describeEntry(entry)` — `describeCreation`
 * (below, right after `describeEntry`) builds a narrative sentence from the SAME real
 * `itemChanges.length`/`costChanges.length`/`paymentChanges.length`/`moneyAfter.total` fields the
 * old generic call already read, e.g. "Transaksi dibuat: 2 barang, 1 biaya/tagihan/potongan,
 * 1 pembayaran, Total Rp X" — a zero-count clause (no payments, no cost items) is omitted
 * entirely rather than reading "0 pembayaran," mirroring `describeEntry`'s own combined branch.
 * `describeEntry` itself is completely UNCHANGED for every non-creation entry.
 * The OLD synthetic-PERTAMA block (hardcoded "Transaksi dibuat — Total Rp X" text, the frozen
 * `pertamaTotal`/`pertamaDibayar` computed by peeking at the oldest REAL edit's own
 * `moneyBefore`) is KEPT, byte-for-byte unchanged, as the fallback for any transaction whose
 * `editLog[0]` is NOT a real creation entry — every already-persisted pre-Piece-3 transaction,
 * which this app deliberately never migrates/backfills (see the root CLAUDE.md's own "don't
 * fabricate history we can't truthfully reconstruct" precedent, e.g. the multi-branch cabang
 * feature's non-backfill decision).
 *
 * Phase T2b-fix: the PERTAMA node names the transaction's total and whatever was actually
 * paid AT CREATION. **PERTAMA's Dibayar freeze (a follow-up fix to the payment-correction
 * rework)**: originally this was derived LIVE from `txn.payments` filtered to
 * `p.at === txn.createdAt` — correct only by accident, back when `editTransaction` never
 * touched `payments` at all. Once the payment-correction rework let `editTransaction` correct
 * an existing payment's amount IN PLACE (preserving its `id`/`at`), that live filter started
 * leaking the CURRENT corrected amount through this node instead of the true original one.
 * Fixed the same way PERTAMA's Total already was (see below): reads the OLDEST editLog entry's
 * own `moneyBefore.dibayar`, falling back to a live sum only when safe (never edited) or
 * unavoidable (old data). This means PERTAMA no longer shows a per-method breakdown ("Tunai Rp
 * X · Transfer Rp Y") — just a single frozen figure ("Rp X"), matching Total's own presentation
 * exactly; `paymentMethodText` is no longer used in this file as a result.
 *
 * **Complete-edit-history fix**:
 * - **PERTAMA's Total is now genuinely frozen, matching Dibayar's own freeze (see above,
 *   added later).** It used to read `txnTotal(txn)` — LIVE, off the transaction's CURRENT
 *   `lines` — so it silently drifted to match whatever the latest edit left behind. Fixed by
 *   reading the OLDEST editLog entry's own `moneyBefore.total` — a real snapshot taken at that
 *   edit's own time, which by construction is the transaction's state exactly as created
 *   (nothing else could have touched it earlier) — falling back to a live `txnTotal(txn)` only
 *   when the transaction has never been edited (safe: nothing could have changed `lines` yet)
 *   or its oldest entry predates this fix and has no `moneyBefore` (an honest, graceful
 *   degradation for old data, not a fabricated number — same precedent as this app's other
 *   "don't backfill history we can't truthfully reconstruct" decisions).
 * - **Each edit entry now carries its own money snapshot** (`moneyBefore`/`moneyAfter`,
 *   `{total, sisa, dibayar}`, built by `App.js`'s `txnMoneySnapshot` at edit time) — shown in
 *   the expanded detail as three before→after lines, so an edit's real net money effect is
 *   visible, not just the raw field diffs that caused it. Old-shaped entries (from before
 *   this fix) simply lack these fields — the money block is omitted for those, not guessed.
 * - **Item-level diffs are grouped per item**, not interleaved into one flat list —
 *   `App.js`'s `buildTxnEditDiff` now returns `itemChanges: {kind, label, fields}[]`
 *   alongside `summary` (unchanged, header-level-only strings). Each `itemChanges` entry
 *   renders as its own "Barang Diubah/Ditambahkan/Dihapus: <name>" header with `fields`
 *   nested underneath. Old-shaped entries have no `itemChanges` — they fall back to
 *   rendering `summary` as a flat list exactly as this component always has.
 * - **Collapsed-by-default, one-line summary per entry** (PERTAMA included) — a transaction
 *   with a long edit history used to render every entry's full detail unconditionally,
 *   growing the modal taller with every edit ever made. A local `expanded` Set (keyed by
 *   entry index, PERTAMA using `"pertama"`) starts EMPTY; clicking a node's summary line
 *   toggles it. No existing collapse/expand precedent existed anywhere in this codebase to
 *   reuse (the one historical analog — Penjualan/Pembelian's own inline history-clock row
 *   expand — was fully removed in Phase T2b-fix) — this is a new, narrow pattern.
 * - **`paymentChanges: {kind, label, fields}[]`** (same shape as `itemChanges`, reusing
 *   `ItemChangeBlock`'s own visual convention via a near-identical `PaymentChangeBlock`) rides
 *   on top of the existing `summary`/`itemChanges`/`moneyBefore`/`moneyAfter` fields.
 *   Originally (Bayar Tagihan) only `recordPayment`/`correctPayment` — which never touched
 *   lines — produced this, so it never co-occurred with `itemChanges` in one entry. **The
 *   payment-correction rework changed that**: `editTransaction` itself can now write
 *   `paymentChanges` (via its own `patch.payments`, diffed by `App.js`'s `buildPaymentDiff`),
 *   including alongside `itemChanges` in the SAME entry when a save corrects both an item and
 *   a payment at once. `describeEntry` has three branches for this — payment-only (kinds:
 *   `payment_added`/`payment_corrected`/`payment_removed`), combined item+payment (new), and
 *   item-only/header-only (original) — see that function's own comment for the exact wording
 *   of each. Old entries simply lack `paymentChanges` — degrades via the same
 *   `entry.paymentChanges || []` guard every other optional field here already uses.
 * - **Fixed-height scroll**: `.editlog-timeline` itself gained `max-height`/`overflow-y`
 *   (styles.css) — reusing `.riwayat-scroll`'s own 360px value rather than inventing a new
 *   number — so a transaction with many edits stays contained in a short scrollable box
 *   instead of growing `TransactionDetailModal` indefinitely. Not paginated — every entry is
 *   already collapsed to one line, so scrolling inside the fixed box is the only interaction
 *   needed.
 *
 * **Cost items Brief 3**: `costChanges: {kind, label, type, fields}[]` (`App.js`'s
 * `buildCostItemDiff`, id-matched like `paymentChanges`) joins `itemChanges`/`paymentChanges`
 * — rendered via a near-identical `CostChangeBlock`, whose header is composed dynamically from
 * the row's own `type` ("Biaya"/"Tagihan"/"Tagihan Supplier" + Ditambahkan/Dikoreksi/Dihapus)
 * rather than a static kind→label map, since the type is what determines whether the change
 * moved Total. That distinction needed NO new money-rendering code, though — `MoneyBlock`
 * already renders Total/Sisa/Dibayar ONCE per entry from `moneyBefore`/`moneyAfter` (which
 * already include costItems as of Brief 2's `txnTotal`), so a Tagihan change already shows a
 * Total arrow and a Biaya change already doesn't, purely because `total` did or didn't
 * actually move. `describeEntry` treats `costChanges` as parallel to `itemChanges` throughout
 * (see that function's own comment) — both can move Total, so they share its delta-suffix
 * rule; only the payment-only branch stays untouched.
 */
import { useState } from "react";
import { fmtDate, fmtQty } from "../utils/idGenerators";
import { txnTotal } from "../utils/txnStatus";
import { costItemTypeLabel } from "../utils/costItems";

const ITEM_HEADER = {
  changed: "Barang Diubah",
  swapped: "Barang Diubah",
  added: "Barang Ditambahkan",
  removed: "Barang Dihapus",
};

const PAYMENT_HEADER = {
  payment_added: "Pembayaran Ditambahkan",
  payment_corrected: "Pembayaran Dikoreksi",
  payment_removed: "Pembayaran Dihapus",
};

/** One-line collapsed summary for an edit entry — item/field counts plus a money delta only
 *  when the total actually changed (a header-only edit, e.g. just Tanggal, leaves money
 *  unchanged, so no arrow is shown for it).
 *
 *  Payment-correction rework: `editTransaction` can now produce a `paymentChanges` array on
 *  its OWN (via `patch.payments`), possibly alongside `itemChanges` in the SAME entry (a
 *  combined item+payment edit in one Simpan) — this is genuinely new; before this rework, only
 *  `recordPayment`/`correctPayment` (never touching lines) produced `paymentChanges`, so it
 *  never co-occurred with `itemChanges`. Three branches now:
 *   1. Payment-only (no item/cost changes, paymentChanges non-empty): if every change in the
 *      batch shares one `kind`, use that kind's literal wording ("Pembayaran"/"Pembayaran
 *      Dikoreksi"/"Pembayaran Dihapus"); a batch mixing kinds (e.g. one row added, one
 *      corrected, one removed, all in the same Simpan) falls back to "N perubahan pembayaran".
 *      Sisa-delta suffix only (never Total — a payment-only entry never moves the total).
 *   2. Combined (item and/or cost changes present, together with payment changes): "N barang
 *      diubah · M biaya/tagihan/potongan diubah · K pembayaran diubah" (whichever legs are non-empty,
 *      + "· J field lain" if header fields also changed), followed by a Total delta suffix
 *      AND/OR a Sisa delta suffix, independently — a combined edit can move either, both, or
 *      neither.
 *   3. Item-and/or-cost-only, or header-only (original behavior, widened to include cost).
 *
 *  Brief 3: `costChanges` (biaya/tagihan/tagihanSupplier/potongan row edits) is treated as PARALLEL to
 *  `itemChanges` throughout — both can move Total (a goods-line price edit or a
 *  tagihan/tagihanSupplier row both do; a biaya-only change never does, but that's already
 *  handled for free by the `moneyBefore.total !== moneyAfter.total` check itself, not by any
 *  special-casing here) — so every place this function used to branch/count on
 *  `itemChanges.length` now also considers `costChanges.length`, via the shared
 *  `hasItemOrCost` flag. Only the payment-only branch's Sisa-only suffix stays untouched,
 *  since a payment-only entry (by construction) never has item or cost changes either. */
/** Bug fix ("warn before abandoning a number" batch — the branch-only `numberingChange` case):
 *  the ONE place both the collapsed-line text (`describeEntry`, below) and the expanded
 *  `NumberingChangeBlock` heading decide whether to say "Cabang & No. Invoice Diperbarui" or
 *  just "Cabang Diperbarui" — a branch-only move (same salesperson, no re-mint, App.js's own
 *  `branchChanged` without `personChanged`) carries an IDENTICAL before/after invoiceNumber, so
 *  claiming the invoice number was also "Diperbarui" would be false. Single source of truth so
 *  the collapsed summary and the expanded detail can never disagree about which heading applies. */
function numberingSummaryLabel(numberingChange) {
  return numberingChange.before.invoiceNumber !== numberingChange.after.invoiceNumber
    ? "Cabang & No. Invoice Diperbarui"
    : "Cabang Diperbarui";
}

/** "1 barang dihapus · 2 barang diubah" — one count per KIND of line change (fix B, section E2).
 *  It used to say "N barang diubah" for every kind, so a removal read as a change. An entry made
 *  only of ordinary changes still reads exactly "N barang diubah", as before. */
function itemCountParts(itemChanges) {
  const order = [["changed", "diubah"], ["added", "ditambahkan"], ["removed", "dihapus"], ["swapped", "diganti"]];
  const parts = [];
  for (const [kind, word] of order) {
    const n = itemChanges.filter((c) => c.kind === kind).length;
    if (n > 0) parts.push(`${n} barang ${word}`);
  }
  const other = itemChanges.filter((c) => !order.some(([k]) => k === c.kind)).length;
  if (other > 0) parts.push(`${other} barang diubah`);
  return parts;
}

function describeEntry(entry) {
  const itemChanges = entry.itemChanges || [];
  const paymentChanges = entry.paymentChanges || [];
  const costChanges = entry.costChanges || [];
  // Salesperson-correction re-mint feature: a single nullable fact (never an array, since a
  // transaction can only ever have ONE current branch/invoice-number attribution at a time —
  // unlike itemChanges/paymentChanges/costChanges, which are genuinely lists). Additive across
  // all three branches below, the same way costChanges was folded in as parallel to
  // itemChanges — this can co-occur with any of them (a salesperson correction made alongside
  // an item/payment edit in the same Simpan), and is surfaced explicitly rather than folded
  // into the generic "N field lain" count, since overriding invariant 8's own "the number is on
  // paper in the world" reasoning is a materially bigger event than an ordinary field edit.
  const hasNumbering = !!entry.numberingChange;
  const otherCount = (entry.summary || []).length;
  const hasItemOrCost = itemChanges.length > 0 || costChanges.length > 0;

  if (!hasItemOrCost && paymentChanges.length > 0) {
    const kinds = new Set(paymentChanges.map((c) => c.kind));
    let line;
    if (kinds.size === 1) {
      const kind = paymentChanges[0].kind;
      line = kind === "payment_corrected" ? "Pembayaran Dikoreksi"
        : kind === "payment_removed" ? "Pembayaran Dihapus"
        : "Pembayaran";
    } else {
      line = `${paymentChanges.length} perubahan pembayaran`;
    }
    if (hasNumbering) line += " · " + numberingSummaryLabel(entry.numberingChange);
    if (entry.moneyBefore && entry.moneyAfter && entry.moneyBefore.sisa !== entry.moneyAfter.sisa) {
      line += ` · Sisa Rp ${fmtQty(entry.moneyBefore.sisa)} → Rp ${fmtQty(entry.moneyAfter.sisa)}`;
    }
    return line;
  }

  if (hasItemOrCost && paymentChanges.length > 0) {
    const parts = [];
    parts.push(...itemCountParts(itemChanges));
    if (costChanges.length > 0) parts.push(`${costChanges.length} biaya/tagihan/potongan diubah`);
    parts.push(`${paymentChanges.length} pembayaran diubah`);
    if (otherCount > 0) parts.push(`${otherCount} field lain`);
    if (hasNumbering) parts.push(numberingSummaryLabel(entry.numberingChange));
    let line = parts.join(" · ");
    if (entry.moneyBefore && entry.moneyAfter) {
      const deltas = [];
      if (entry.moneyBefore.total !== entry.moneyAfter.total) {
        deltas.push(`Total Rp ${fmtQty(entry.moneyBefore.total)} → Rp ${fmtQty(entry.moneyAfter.total)}`);
      }
      if (entry.moneyBefore.sisa !== entry.moneyAfter.sisa) {
        deltas.push(`Sisa Rp ${fmtQty(entry.moneyBefore.sisa)} → Rp ${fmtQty(entry.moneyAfter.sisa)}`);
      }
      if (deltas.length > 0) line += ` · ${deltas.join(" · ")}`;
    }
    return line;
  }

  const parts = [];
  parts.push(...itemCountParts(itemChanges));
  if (costChanges.length > 0) parts.push(`${costChanges.length} biaya/tagihan/potongan diubah`);
  if (otherCount > 0) parts.push(hasItemOrCost ? `${otherCount} field lain` : `${otherCount} perubahan`);
  if (hasNumbering) parts.push(numberingSummaryLabel(entry.numberingChange));
  if (parts.length === 0) parts.push("Perubahan");
  let line = parts.join(" · ");
  if (entry.moneyBefore && entry.moneyAfter && entry.moneyBefore.total !== entry.moneyAfter.total) {
    line += ` · Total Rp ${fmtQty(entry.moneyBefore.total)} → Rp ${fmtQty(entry.moneyAfter.total)}`;
  }
  return line;
}

/** PERTAMA-only collapsed line — a narrative sentence, distinct from describeEntry's generic
 *  diff wording (used by every non-creation entry). Called ONLY when entry.isCreation is true;
 *  never touches describeEntry's own branches/output for any other entry. Zero-count clauses
 *  (no payments, no cost items) are omitted entirely rather than rendering "0 pembayaran" —
 *  mirrors describeEntry's own combined branch, which likewise only pushes a `parts` entry
 *  when a count is > 0. */
function describeCreation(entry) {
  const itemChanges = entry.itemChanges || [];
  const paymentChanges = entry.paymentChanges || [];
  const costChanges = entry.costChanges || [];
  const parts = [];
  if (itemChanges.length > 0) parts.push(`${itemChanges.length} barang`);
  if (costChanges.length > 0) parts.push(`${costChanges.length} biaya/tagihan/potongan`);
  if (paymentChanges.length > 0) parts.push(`${paymentChanges.length} pembayaran`);
  const total = entry.moneyAfter ? entry.moneyAfter.total : 0;
  const totalPart = `Total Rp ${fmtQty(total)}`;
  return parts.length > 0
    ? `Transaksi dibuat: ${parts.join(", ")}, ${totalPart}`
    : `Transaksi dibuat: ${totalPart}`;
}

function MoneyBlock({ before, after }) {
  const row = (label, a, b, color) => (
    <div className="editlog-money__row" style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: "var(--gap-xs)" }}>
      <span>{label}</span>
      <span style={color ? { color } : undefined}>
        Rp {fmtQty(a)}{a !== b ? ` → Rp ${fmtQty(b)}` : ""}
      </span>
    </div>
  );
  return (
    <div className="editlog-money" style={{ marginBottom: "var(--gap-sm)" }}>
      {row("Total Nilai", before.total, after.total)}
      {row("Sudah Dibayar", before.dibayar, after.dibayar, "var(--green)")}
      {row("Sisa Tagihan", before.sisa, after.sisa, "#92400e")}
    </div>
  );
}

function ItemChangeBlock({ change }) {
  return (
    <div className="editlog-item" style={{ marginTop: "var(--gap-sm)" }}>
      <div className="editlog-item__header" style={{ fontSize: 13, fontWeight: 600, color: "var(--blue-deep)" }}>
        {ITEM_HEADER[change.kind] || "Barang Diubah"}: {change.label}
      </div>
      {change.fields.length > 0 && (
        <ul className="editlog-node__list editlog-item__fields">
          {change.fields.map((f, i) => <li key={i}>{f}</li>)}
        </ul>
      )}
    </div>
  );
}

/** Mirrors ItemChangeBlock exactly (same {kind, label, fields} shape, same markup) — Phase T3's
 *  reuse of the existing per-entry visual convention rather than a new format, per design. */
function PaymentChangeBlock({ change }) {
  return (
    <div className="editlog-item" style={{ marginTop: "var(--gap-sm)" }}>
      <div className="editlog-item__header" style={{ fontSize: 13, fontWeight: 600, color: "var(--blue-deep)" }}>
        {PAYMENT_HEADER[change.kind] || "Pembayaran"}: {change.label}
      </div>
      {change.fields.length > 0 && (
        <ul className="editlog-node__list editlog-item__fields">
          {change.fields.map((f, i) => <li key={i}>{f}</li>)}
        </ul>
      )}
    </div>
  );
}

const COST_KIND_VERB = { cost_added: "Ditambahkan", cost_corrected: "Dikoreksi", cost_removed: "Dihapus" };

/** Mirrors ItemChangeBlock/PaymentChangeBlock's markup exactly (same {kind, label, fields}
 *  shape, same visual convention), with one deliberate deviation: the header is composed
 *  dynamically ("{Biaya|Tagihan|Tagihan Supplier} {Ditambahkan|Dikoreksi|Dihapus}") instead of
 *  a static kind→label map, since which TYPE a cost item is carries real meaning here — a
 *  Biaya change never moves Total, a Tagihan/Tagihan Supplier change does. That distinction
 *  itself needs no code here, though: it's already visible for free via the entry's own
 *  MoneyBlock (below), which only shows a Total arrow when total genuinely changed. */
function CostChangeBlock({ change, txnType }) {
  const typeLabel = costItemTypeLabel(change.type, txnType);
  return (
    <div className="editlog-item" style={{ marginTop: "var(--gap-sm)" }}>
      <div className="editlog-item__header" style={{ fontSize: 13, fontWeight: 600, color: "var(--blue-deep)" }}>
        {typeLabel} {COST_KIND_VERB[change.kind] || "Diubah"}: {change.label}
      </div>
      {change.fields.length > 0 && (
        <ul className="editlog-node__list editlog-item__fields">
          {change.fields.map((f, i) => <li key={i}>{f}</li>)}
        </ul>
      )}
    </div>
  );
}

/** Salesperson-correction re-mint feature: a NEW, genuinely different kind of change from
 *  itemChanges/paymentChanges/costChanges — those are all arrays (a transaction can have many
 *  changed lines/payments/cost rows in one edit), but a transaction can only ever have ONE
 *  current branch/invoice-number attribution, so `entry.numberingChange` is a single nullable
 *  `{before, after}` object, not a list. Mirrors ItemChangeBlock/PaymentChangeBlock/
 *  CostChangeBlock's exact markup convention for visual consistency, despite the different
 *  shape underneath. `branchName` on each side is already resolved server-side (App.js, via
 *  `resolveBranchName`, falling back to the generic "Cabang" label the same way every other
 *  reader of that function does) — this component never re-resolves an id itself.
 *
 *  **Bug fix ("the re-mint trigger compares roles, not people"): `numberingChange` now also
 *  fires on a branch-only move (same salesperson, different branch — App.js's own
 *  `branchChanged` without `personChanged`), where the invoice number is deliberately left
 *  unchanged (the counter is per-person; nothing about who's billed moved).** Rendering the
 *  existing two-line "X → Y" shape unconditionally would print "No. Invoice: BD 0007/VII/26 →
 *  BD 0007/VII/26" — true, but reads as though something changed that didn't. `numberingSummaryLabel`
 *  (above `describeEntry` — the SAME function that function's own collapsed line already uses, so
 *  the two can never disagree) checks the two invoice-number strings directly: equal → heading
 *  reads "Cabang Diperbarui" and the invoice-number line is omitted entirely (nothing to report
 *  there); different → the original "Cabang & No. Invoice Diperbarui" heading with both lines,
 *  unchanged from before. */
function NumberingChangeBlock({ change }) {
  const hasNewNumber = change.before.invoiceNumber !== change.after.invoiceNumber;
  return (
    <div className="editlog-item" style={{ marginTop: "var(--gap-sm)" }}>
      <div className="editlog-item__header" style={{ fontSize: 13, fontWeight: 600, color: "var(--blue-deep)" }}>
        {numberingSummaryLabel(change)}
      </div>
      <ul className="editlog-node__list editlog-item__fields">
        <li>Cabang: {change.before.branchName} → {change.after.branchName}</li>
        {hasNewNumber && <li>No. Invoice: {change.before.invoiceNumber} → {change.after.invoiceNumber}</li>}
      </ul>
    </div>
  );
}

/** The shared per-entry node — collapsed one-line summary (via `describeEntry`) that expands
 *  to the money block + grouped item/payment/cost change blocks. Extracted (Piece 3 of the
 *  corrective pass) so a real creation entry (tagged PERTAMA) and every later edit entry
 *  (tagged DETAIL PERUBAHAN) can share IDENTICAL rendering — the only difference between them
 *  is the `tag` string itself, never a second, separately-maintained formatting path. */
function EditLogEntryNode({ tag, entry, isOpen, onToggle, txnType }) {
  const itemChanges = entry.itemChanges || [];
  const paymentChanges = entry.paymentChanges || [];
  const costChanges = entry.costChanges || [];
  return (
    <div className="editlog-node">
      <span className="editlog-node__tag">{tag}</span>
      <span className="editlog-node__time">{fmtDate(entry.at)}</span>
      <button type="button" className="editlog-node__summary" onClick={onToggle}>
        <span className="editlog-node__chevron">{isOpen ? "▾" : "▸"}</span>
        <span>{entry.isCreation ? describeCreation(entry) : describeEntry(entry)}</span>
      </button>
      {isOpen && (
        <div className="editlog-node__detail">
          {entry.moneyBefore && entry.moneyAfter && (
            <MoneyBlock before={entry.moneyBefore} after={entry.moneyAfter} />
          )}
          {itemChanges.map((change, j) => <ItemChangeBlock key={j} change={change} />)}
          {paymentChanges.map((change, j) => <PaymentChangeBlock key={j} change={change} />)}
          {costChanges.map((change, j) => <CostChangeBlock key={j} change={change} txnType={txnType} />)}
          {/* A single nullable object, not an array-shaped optional field like the three
              above — a plain truthy guard, not the `|| []` convention, since `numberingChange`
              can only ever be one linked fact (see NumberingChangeBlock's own comment). An old
              entry simply lacks the key (`undefined`), rendering nothing here, same as today. */}
          {entry.numberingChange && <NumberingChangeBlock change={entry.numberingChange} />}
          {(entry.summary || []).length > 0 && (
            <ul className="editlog-node__list">
              {entry.summary.map((line, j) => <li key={j}>{line}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default function EditLogTimeline({ txn }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = (key) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const editLog = txn.editLog || [];
  // Piece 3 of the corrective pass: a transaction created after this shipped has a REAL,
  // fully-itemized creation entry at editLog[0] (App.js's createTransaction), marked with
  // `isCreation: true` so this component can tell it apart from an old transaction's own
  // genuine first EDIT landing at editLog[0] (pre-Piece-3 data — never migrated/backfilled,
  // per this app's own "don't fabricate history we can't truthfully reconstruct" precedent).
  const hasCreationEntry = editLog[0]?.isCreation === true;
  const creationEntry = hasCreationEntry ? editLog[0] : null;
  const laterEntries = hasCreationEntry ? editLog.slice(1) : editLog;
  const pertamaOpen = expanded.has("pertama");

  // Frozen creation total: the oldest edit's own moneyBefore.total is a real snapshot taken
  // at that edit's time, which is by construction the transaction's state as created. Falls
  // back to a live computation only when safe (never edited) or unavoidable (old data).
  // ONLY used by the legacy synthetic-PERTAMA fallback below (`!hasCreationEntry`) — a real
  // creation entry already carries its own genuine moneyBefore/moneyAfter, read directly by
  // EditLogEntryNode via the shared MoneyBlock, so this freeze workaround is structurally
  // unneeded (not merely unused) once `hasCreationEntry` is true.
  const pertamaTotal = editLog[0]?.moneyBefore ? editLog[0].moneyBefore.total : txnTotal(txn);
  // Frozen creation Dibayar — mirrors pertamaTotal's own freeze pattern exactly. Previously
  // this read live off txn.payments filtered by at === createdAt, which was only ever
  // accidentally correct because editTransaction never touched payments; now that it can
  // (payment-correction rework), a corrected creation-time row's CURRENT amount would leak
  // through here, misrepresenting what was truly paid at creation. Falls back to a live
  // computation only when safe (never edited) or unavoidable (old data predating this fix) —
  // identical fallback shape to pertamaTotal. Note this necessarily drops the old per-method
  // breakdown ("Tunai Rp X · Transfer Rp Y") in favor of a single frozen figure, since
  // moneyBefore only ever stores the three aggregate numbers, never a per-payment snapshot —
  // same tradeoff pertamaTotal itself already made. Same "legacy fallback only" scope as above.
  const pertamaDibayar = editLog[0]?.moneyBefore
    ? editLog[0].moneyBefore.dibayar
    : (txn.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);

  return (
    <div className="editlog-timeline">
      {creationEntry ? (
        <EditLogEntryNode
          tag="PERTAMA"
          entry={creationEntry}
          isOpen={pertamaOpen}
          onToggle={() => toggle("pertama")}
          txnType={txn.type}
        />
      ) : (
        <div className="editlog-node">
          <span className="editlog-node__tag">PERTAMA</span>
          <span className="editlog-node__time">{fmtDate(txn.createdAt)}</span>
          <button type="button" className="editlog-node__summary" onClick={() => toggle("pertama")}>
            <span className="editlog-node__chevron">{pertamaOpen ? "▾" : "▸"}</span>
            <span>Transaksi dibuat — Total Rp {fmtQty(pertamaTotal)}</span>
          </button>
          {pertamaOpen && (
            <div className="editlog-node__detail">
              <div style={{ fontSize: 13, marginTop: "var(--gap-xs)" }}>Total Rp {fmtQty(pertamaTotal)}</div>
              <div style={{ fontSize: 13, marginTop: "var(--gap-xs)", color: "var(--muted)" }}>
                {pertamaDibayar > 0 ? `Dibayar Rp ${fmtQty(pertamaDibayar)}` : "Belum ada pembayaran."}
              </div>
            </div>
          )}
        </div>
      )}
      {laterEntries.length === 0 ? (
        <div className="form-hint" style={{ padding: "8px 0" }}>Belum ada perubahan.</div>
      ) : laterEntries.map((entry, i) => (
        <EditLogEntryNode
          key={i}
          tag="DETAIL PERUBAHAN"
          entry={entry}
          isOpen={expanded.has(i)}
          onToggle={() => toggle(i)}
          txnType={txn.type}
        />
      ))}
    </div>
  );
}
